import React, { useState, useEffect } from 'react';
import { Form, Input, DatePicker, InputNumber, Select, message, Upload, Switch, Button, Card, Modal, Alert } from 'antd';
import { UploadOutlined, AppstoreOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import { TournamentService } from '@/services/tournament.service';
import { ClubService } from '@/services/club.service';
import apiService from '@/services/api.service';
import { API_ENDPOINTS } from '@/constants/api.constants';
import type { Tournament } from '@/types/api.types';
import type { Club } from '@/types/api.types';
import { TOURNAMENT_STATUS, TOURNAMENT_STATUS_LABELS } from '@/constants/tournament.constants';
import { logger } from '@/utils/logger';
import { useAuth } from '@/contexts/AuthContext';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { customLocale, getDateValueFromEvent, datePickerProps } from '@/utils/datePickerUtils';

// Настраиваем dayjs для русской локализации
dayjs.locale('ru');

interface TournamentEditFormProps {
  tournament: Tournament | null;
  onSuccess?: () => void;
}

export const TournamentEditForm: React.FC<TournamentEditFormProps> = ({
  tournament,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loadingClubs, setLoadingClubs] = useState(false);
  const [formReady, setFormReady] = useState(false);
  const [documentFileList, setDocumentFileList] = useState<UploadFile[]>([]);
  const [coverImageFileList, setCoverImageFileList] = useState<UploadFile[]>([]);
  const [tournamentData, setTournamentData] = useState<Tournament | null>(null);
  const { user } = useAuth();
  const [isGenerateBracketsModalVisible, setIsGenerateBracketsModalVisible] = useState(false);
  const [generatingBrackets, setGeneratingBrackets] = useState(false);
  const [numTatami, setNumTatami] = useState<number>(1);
  const [hasMatches, setHasMatches] = useState(false);
  const [checkingMatches, setCheckingMatches] = useState(true);

  // Отслеживаем готовность формы
  useEffect(() => {
    if (form) {
      setFormReady(true);
    }
  }, [form]);

  // Загружаем актуальные данные турнира
  useEffect(() => {
    const loadTournamentData = async () => {
      if (tournament?.id) {
        try {
          const freshTournamentData = await TournamentService.getById(tournament.id);
          setTournamentData(freshTournamentData);
          
          // Проверяем наличие матчей
          try {
            setCheckingMatches(true);
            const matches = await TournamentService.getTournamentMatches(tournament.id);
            const matchesArray = Array.isArray(matches) ? matches : (matches as any)?.data || [];
            const hasMatchesResult = matchesArray.length > 0;
            setHasMatches(hasMatchesResult);            
          } catch (error) {
            logger.error('Error checking matches:', error);
            setHasMatches(false);
          } finally {
            setCheckingMatches(false);
          }
        } catch (error) {
          logger.error('Error loading tournament data:', error);
          setTournamentData(tournament);
        }
      } else {
        setTournamentData(tournament);
      }
    };

    loadTournamentData();
  }, [tournament?.id]);

  useEffect(() => {
    fetchClubs();
  }, []);

  const fetchClubs = async () => {
    try {
      setLoadingClubs(true);
      const response = await ClubService.getClubs({ limit: 1000 });
      const clubsList = Array.isArray(response.data) ? response.data : response.data.rows || [];
      setClubs(clubsList);
    } catch (error) {
      logger.error('Error loading clubs:', error);
      message.warning('Не удалось загрузить список клубов');
    } finally {
      setLoadingClubs(false);
    }
  };

  // Инициализация формы
  useEffect(() => {
    if (tournamentData && form && formReady) {
      if (tournamentData.coverImage) {
        setCoverImageFileList([{
          uid: '-1',
          name: 'cover.jpg',
          status: 'done',
          url: tournamentData.coverImage,
        }]);
      }

      form.resetFields();
      
      setTimeout(() => {
        try {
          if (form) {
            const invitedClubIds = tournamentData.invitedClubIds || [];
            
            const autoRegistration = typeof tournamentData.autoRegistration === 'string'
              ? tournamentData.autoRegistration === 'true'
              : tournamentData.autoRegistration;
            const isInviteOnly = typeof tournamentData.isInviteOnly === 'string'
              ? tournamentData.isInviteOnly === 'true'
              : tournamentData.isInviteOnly;
            const hasTwoThirdPlaces = typeof tournamentData.hasTwoThirdPlaces === 'string'
              ? tournamentData.hasTwoThirdPlaces === 'true'
              : tournamentData.hasTwoThirdPlaces;
            
            const formValues = {
              name: tournamentData.name,
              description: tournamentData.description,
              location: tournamentData.location,
              tournamentDate: dayjs(tournamentData.tournamentDate),
              registrationDeadline: dayjs(tournamentData.registrationDeadline),
              weighingDates: tournamentData.weighingDates?.map(date => dayjs(date)),
              feeForClubMembers: tournamentData.feeForClubMembers,
              feeForOthers: tournamentData.feeForOthers,
              status: tournamentData.status,
              autoRegistration: autoRegistration,
              isInviteOnly: isInviteOnly,
              hasTwoThirdPlaces: hasTwoThirdPlaces,
              invitedClubIds: invitedClubIds,
            };
            
            form.setFieldsValue(formValues);
          }
        } catch (error) {
          logger.error('Error initializing form:', error);
        }
      }, 0);
    }
  }, [tournamentData, form, formReady]);

  const handleSubmit = async () => {
    if (!tournamentData || !form) {
      logger.error('Tournament or form is not available');
      return;
    }

    const tournamentId = tournamentData.id;

    try {
      setSubmitting(true);
      const values = await form.validateFields();

      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('description', values.description || '');
      formData.append('location', values.location);
      formData.append('tournamentDate', values.tournamentDate.format('YYYY-MM-DD'));
      formData.append('registrationDeadline', values.registrationDeadline.format('YYYY-MM-DD'));
      
      if (values.weighingDates && Array.isArray(values.weighingDates)) {
        values.weighingDates.forEach((date: dayjs.Dayjs, index: number) => {
          formData.append(`weighingDates[${index}]`, date.format('YYYY-MM-DD'));
        });
      }
      
      formData.append('feeForClubMembers', String(values.feeForClubMembers || 0));
      formData.append('feeForOthers', String(values.feeForOthers || 0));
      formData.append('status', values.status);
      formData.append('autoRegistration', String(values.autoRegistration || false));
      formData.append('isInviteOnly', String(values.isInviteOnly || false));
      formData.append('hasTwoThirdPlaces', String(values.hasTwoThirdPlaces || false));

      if (values.isInviteOnly && values.invitedClubIds && Array.isArray(values.invitedClubIds)) {
        values.invitedClubIds.forEach((clubId: number, index: number) => {
          formData.append(`invitedClubIds[${index}]`, String(clubId));
        });
      }

      if (coverImageFileList.length > 0 && coverImageFileList[0].originFileObj) {
        formData.append('files', coverImageFileList[0].originFileObj);
      }

      documentFileList.forEach((file) => {
        if (file.originFileObj) {
          formData.append('files', file.originFileObj);
        }
      });

      await apiService.put(`${API_ENDPOINTS.TOURNAMENTS.BASE}/${tournamentId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      message.success('Турнир успешно обновлен');
      form.resetFields();
      setDocumentFileList([]);
      setCoverImageFileList([]);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      logger.error('Error updating tournament:', error);
      message.error('Ошибка при обновлении турнира');
    } finally {
      setSubmitting(false);
    }
  };

  const beforeUpload = (file: File) => {
    const isImage = file.type.startsWith('image/');
    if (isImage) {
      message.error('Для загрузки изображений используйте поле "Фото турнира". Здесь можно загружать только документы!');
      return false;
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('Файл должен быть меньше 10MB!');
      return false;
    }
    return false;
  };

  const beforeUploadCoverImage = (file: File) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('Можно загружать только изображения!');
      return false;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('Изображение должно быть меньше 5MB!');
      return false;
    }
    return false;
  };

  const handleDocumentChange = (info: any) => {
    setDocumentFileList(info.fileList);
  };

  const handleCoverImageChange = (info: any) => {
    setCoverImageFileList(info.fileList.slice(-1));
  };

  // Проверка прав доступа для формирования пулов
  const isAdmin = user?.role === 'admin';
  const isOrganizer = tournamentData?.organizerId === user?.id;
  const isManagerOrCoach = user?.role === 'manager' || user?.role === 'coach' || user?.role === 'head_manager';
  const isTournamentClubMember = tournamentData?.clubId && user?.clubId && tournamentData.clubId === user.clubId;
  
  // Кнопка доступна, если есть участники, турнир опубликован и нет записей в таблице боев
  const canGenerateBrackets = (isAdmin || isOrganizer || (isManagerOrCoach && isTournamentClubMember)) &&
    tournamentData?.status === 'published' &&
    !checkingMatches &&
    (tournamentData?.participantsCount || 0) > 0 &&
    !hasMatches;
  
  // Логирование для отладки
  useEffect(() => {
    if (tournamentData) {
      
    }
  }, [tournamentData, isAdmin, isOrganizer, isManagerOrCoach, isTournamentClubMember, hasMatches, checkingMatches, canGenerateBrackets]);

  const handleGenerateBrackets = async () => {
    if (!tournamentData?.id) {
      logger.error('Cannot generate brackets: tournament ID is missing');
      message.error('Ошибка: не указан ID турнира');
      return;
    }
    
    console.group('🚀 [TournamentEditForm] Начало генерации пулов');
    console.log('📋 Данные турнира:', {
      id: tournamentData.id,
      name: tournamentData.name,
      status: tournamentData.status,
      numTatami
    });
    
    try {
      setGeneratingBrackets(true);
      const result = await TournamentService.generateBrackets(tournamentData.id, numTatami);
      console.log('✅ Результат генерации:', result);
      
      // Проверяем результат
      if (result && (result.success !== false)) {        
        message.success('Пулы успешно сформированы');
        setIsGenerateBracketsModalVisible(false);
        setHasMatches(true);
        
        // Вызываем onSuccess для обновления данных турнира
        if (onSuccess) {
          await onSuccess();
        }
        
        // Обновляем данные турнира и проверяем наличие матчей
        try {
          // Небольшая задержка, чтобы сервер успел сохранить матчи
          await new Promise(resolve => setTimeout(resolve, 500));
          
          const updatedTournament = await TournamentService.getById(tournamentData.id);
          setTournamentData(updatedTournament);
          
          // Проверяем наличие матчей после формирования
          const matches = await TournamentService.getTournamentMatches(tournamentData.id);
          const matchesArray = Array.isArray(matches) ? matches : (matches as any)?.data || [];
          const matchesCount = matchesArray.length;
          setHasMatches(matchesCount > 0);                   
          
          if (matchesCount === 0) {
            logger.warn('No matches found after bracket generation. This might indicate an issue.');
            message.warning('Пулы сформированы, но матчи не найдены. Попробуйте обновить страницу.');
          } else {
            message.success(`Пулы успешно сформированы. Создано ${matchesCount} матчей.`);
          }
        } catch (updateError) {
          logger.error('Error updating tournament data:', updateError);
          message.warning('Пулы могут быть сформированы, но не удалось проверить результат. Обновите страницу.');
        }
      } else {
        logger.warn('Brackets generation returned unexpected result:', result);
        message.warning('Пулы могут быть не полностью сформированы. Проверьте результат.');
      }
    } catch (error: any) {
      logger.error('Error generating brackets:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Ошибка при формировании пулов';
      message.error(errorMessage);
      
      // Показываем более детальную информацию об ошибке в консоли
      if (error?.response) {
        logger.error('API Error details:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        });
      }
    } finally {
      setGeneratingBrackets(false);
    }
  };

  if (!tournamentData) {
    return <div>Загрузка данных турнира...</div>;
  }

  return (
    <>
      {canGenerateBrackets && (
        <Card style={{ marginBottom: '24px' }}>
          <Alert
            message="Пулы еще не сформированы"
            description="После завершения регистрации участников сформируйте пулы для создания сеток турнира."
            type="info"
            showIcon
            style={{ marginBottom: '16px' }}
          />
          <Button
            type="primary"
            size="large"
            icon={<AppstoreOutlined />}
            onClick={() => {              
              setIsGenerateBracketsModalVisible(true);
            }}
            style={{ width: '100%' }}
          >
            Сформировать пулы
          </Button>
        </Card>
      )}
      <Card>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Form.Item
          name="coverImage"
          label="Фото турнира"
          extra="Рекомендуемый размер: не более 5MB, форматы: JPEG, PNG"
        >
          <Upload
            listType="picture-card"
            fileList={coverImageFileList}
            onChange={handleCoverImageChange}
            beforeUpload={beforeUploadCoverImage}
            accept="image/*"
            maxCount={1}
            onRemove={() => setCoverImageFileList([])}
          >
            {coverImageFileList.length < 1 && (
              <div>
                <UploadOutlined />
                <div style={{ marginTop: 8 }}>Загрузить</div>
              </div>
            )}
          </Upload>
        </Form.Item>

        <Form.Item
          name="name"
          label="Название"
          rules={[{ required: true, message: 'Введите название турнира' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="description"
          label="Описание"
        >
          <Input.TextArea rows={4} />
        </Form.Item>

        <Form.Item
          name="location"
          label="Место проведения"
          rules={[{ required: true, message: 'Укажите место проведения' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="tournamentDate"
          label="Дата проведения"
          rules={[{ required: true, message: 'Выберите дату проведения' }]}
          getValueFromEvent={getDateValueFromEvent}
        >
          <DatePicker 
            {...datePickerProps}
            placeholder="Выберите дату (ДД.ММ.ГГГГ)"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          name="registrationDeadline"
          label="Окончание регистрации"
          rules={[{ required: true, message: 'Укажите окончание регистрации' }]}
          getValueFromEvent={getDateValueFromEvent}
        >
          <DatePicker 
            {...datePickerProps}
            placeholder="Выберите дату (ДД.ММ.ГГГГ)"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          name="weighingDates"
          label="Даты взвешивания"
        >
          <DatePicker.RangePicker 
            style={{ width: '100%' }}
            format="DD.MM.YYYY"
            locale={customLocale}
            placeholder={['Начало', 'Конец']}
            disabledDate={() => false}
          />
        </Form.Item>

        <Form.Item
          name="feeForClubMembers"
          label="Взнос для членов клуба"
          initialValue={0}
        >
          <InputNumber<number>
            style={{ width: '100%' }}
            min={0}
            precision={2}
            formatter={(value) => `${value} ₽`}
            parser={(value) => {
              const parsed = value?.replace(' ₽', '').replace(/\s/g, '') || '';
              return parsed ? parseFloat(parsed) : 0;
            }}
          />
        </Form.Item>

        <Form.Item
          name="feeForOthers"
          label="Взнос для остальных участников"
          initialValue={0}
        >
          <InputNumber<number>
            style={{ width: '100%' }}
            min={0}
            precision={2}
            formatter={(value) => `${value} ₽`}
            parser={(value) => {
              const parsed = value?.replace(' ₽', '').replace(/\s/g, '') || '';
              return parsed ? parseFloat(parsed) : 0;
            }}
          />
        </Form.Item>

        <Form.Item
          name="autoRegistration"
          label="Автоматическая регистрация"
          valuePropName="checked"
          help="Если включено, регистрация участников подтверждается автоматически без участия организатора"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          name="isInviteOnly"
          label="Турнир по приглашениям"
          valuePropName="checked"
          help="Если включено, турнир будет виден только приглашенным клубам и организатору"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          name="hasTwoThirdPlaces"
          label="Два третьих места"
          valuePropName="checked"
          help="Если включено, количество боев = количество участников - 1 (два 3 места), иначе количество боев = количество участников"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => prevValues.isInviteOnly !== currentValues.isInviteOnly}
        >
          {({ getFieldValue }) =>
            getFieldValue('isInviteOnly') ? (
              <Form.Item
                name="invitedClubIds"
                label="Пригласить клубы"
                rules={[
                  {
                    required: true,
                    message: 'Выберите хотя бы один клуб для приглашения'
                  }
                ]}
                help="Выберите клубы, которые будут приглашены на турнир"
              >
                <Select
                  mode="multiple"
                  placeholder="Выберите клубы"
                  loading={loadingClubs}
                  style={{ width: '100%' }}
                  optionFilterProp="children"
                  showSearch
                  filterOption={(input, option) => {
                    const label = typeof option?.children === 'string' 
                      ? option.children 
                      : String(option?.children || '');
                    return label.toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {clubs.map(club => (
                    <Select.Option key={club.id} value={club.id}>
                      {club.name} {club.city ? `(${club.city})` : ''}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            ) : null
          }
        </Form.Item>

        <Form.Item
          name="status"
          label="Статус"
          initialValue="draft"
        >
          <Select placeholder="Выберите статус турнира" style={{ width: '100%' }}>
            {Object.entries(TOURNAMENT_STATUS)
              .filter(([key]) => key !== 'DELETED')
              .map(([key, value]) => (
                <Select.Option key={key} value={value}>
                  {TOURNAMENT_STATUS_LABELS[value]}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="documents"
          label="Документы"
          extra="Можно загружать документы (PDF, DOC, DOCX и др.). Максимальный размер файла: 10MB"
        >
          <Upload
            fileList={documentFileList}
            onChange={handleDocumentChange}
            beforeUpload={beforeUpload}
            multiple
          >
            <Button icon={<UploadOutlined />}>Загрузить документы</Button>
          </Upload>
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting} size="large">
            Сохранить изменения
          </Button>
        </Form.Item>
      </Form>
    </Card>
    
    <Modal
      title="Сформировать пулы"
      open={isGenerateBracketsModalVisible}
      onOk={handleGenerateBrackets}
      onCancel={() => setIsGenerateBracketsModalVisible(false)}
      confirmLoading={generatingBrackets}
      okText="Сформировать"
      cancelText="Отмена"
    >
      <p>Вы уверены, что хотите сформировать пулы для этого турнира?</p>
      <p>Это действие распределит всех зарегистрированных участников по пулам в соответствии с их категориями.</p>
      <p>При формировании пулов будут учтены следующие условия:</p>
      <ul>
        <li>Максимально развести бойцов от одного тренера в разные ветки</li>
        <li>Максимально развести бойцов с одного клуба</li>
        <li>Максимально развести бойцов с одного города</li>
        <li>Максимально развести бойцов с одной федерации</li>
      </ul>
      <div style={{ marginTop: '16px' }}>
        <label>Количество татами:</label>
        <InputNumber
          min={1}
          max={10}
          value={numTatami}
          onChange={(value) => setNumTatami(value || 1)}
          style={{ width: '100%', marginTop: '8px' }}
        />
      </div>
    </Modal>
    </>
  );
};
